import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Redirect } from 'react-router-dom';

import StashConfirm from './Confirm/Confirm';
import StashTitle from './Title/Title';

import { Email, PhoneNumber } from 'components/forms';
import { InfoDialog, BackButton } from 'components/ui';
import { Divider, PrimaryButton } from '@huggg/react-components';

import { interpolate } from 'utilities/interpolate';
import { getProductName } from 'utilities/huggg';
import { claimRestrictionsValidator } from 'utilities/superhuggg';
import dict from 'config/dictionary';
import styles from './stash.module.scss';

export const InputScreen = (props) => {
  const {
    view,
    stashAction,
    huggg,
    isSuperhugggEmailClaim,
    isSuperhugggSMSClaim,
    email,
    number,
    setRecipientEmail,
    setRecipientNumber,
    stashing,
    error,
    changeRecipientEmail,
    changeRecipientNumber,
  } = props;

  const onPhoneNumberSubmit = useCallback(
    (values) => {
      setRecipientNumber(values.number);
    },
    [setRecipientNumber],
  );

  const onEmailSubmit = useCallback(
    (values) => {
      setRecipientEmail(values.email);
    },
    [setRecipientEmail],
  );

  const reasonDialogRef = useRef();
  const openLinkRef = useRef();

  const renderSubControls = (backIsCancel = false) => (
    <>
      <div
        className={styles.infoLink}
        onClick={() => {
          reasonDialogRef.current.open();
        }}
        ref={openLinkRef}
      >
        {
          dict.stash.privacyInformation.lede[
            isSuperhugggEmailClaim ? 'email' : 'phone'
          ]
        }
      </div>
      {backIsCancel ? (
        <BackButton />
      ) : (
        <BackButton
          onClick={
            isSuperhugggEmailClaim
              ? changeRecipientEmail
              : changeRecipientNumber
          }
        />
      )}
      <InfoDialog
        ref={reasonDialogRef}
        openButtonRef={openLinkRef}
        body={
          <>
            <div className={styles.validationRulesMessage}>
              {
                dict.stash.privacyInformation.lede[
                  isSuperhugggEmailClaim ? 'email' : 'phone'
                ]
              }
            </div>
            <p>
              {interpolate(
                dict.stash.privacyInformation.copy,
                '[br]',
                <br key={null} />,
              )}
            </p>
          </>
        }
      />
    </>
  );

  if (view === 'input') {
    const text = huggg.isSuperhuggg
      ? isSuperhugggSMSClaim
        ? dict.stash.form.submitButton.superhuggg.sms
        : dict.stash.form.submitButton.superhuggg.email
      : dict.stash.form.submitButton.huggg;

    const renderSubmitButton = () => {
      return (
        <PrimaryButton
          label={text
            .replace('[product_name]', getProductName(huggg))
            .toUpperCase()}
          className={styles.submitButton}
        />
      );
    };

    if (isSuperhugggEmailClaim) {
      const getAdditionalValidationError = () => {
        const genericErrorMessage = `Oops! This address doesn't meet the restrictions.`;

        if (huggg.claim_restrictions) {
          return {
            message: `${genericErrorMessage} Must end with ${huggg.claim_restrictions[0]}`,
            buttonText: '(+ more)',
            dialog: {
              title:
                'Acceptable email addresses must match the following endings:',
              body: huggg.claim_restrictions,
            },
          };
        }

        if (huggg.claim_restrict_personal_emails) {
          return {
            message: `Personal email addresses not accepted. Please enter your work email instead.`,
          };
        }

        return { message: genericErrorMessage };
      };

      const additionalValidatorError = getAdditionalValidationError();
      const enableAdditionalValidator =
        huggg.claim_restrictions || huggg.claim_restrict_personal_emails;

      return (
        <>
          <Email
            additionalValidator={
              enableAdditionalValidator
                ? claimRestrictionsValidator(huggg, 'email')
                : undefined
            }
            additionalValidatorError={additionalValidatorError}
            onSubmit={onEmailSubmit}
            data-test-id="stash-submit-form"
            button={renderSubmitButton()}
          />
          {renderSubControls(true)}
        </>
      );
    }

    return (
      <>
        <PhoneNumber
          additionalValidator={
            huggg.claim_restrictions
              ? claimRestrictionsValidator(huggg, 'sms')
              : undefined
          }
          onSubmit={onPhoneNumberSubmit}
          data-test-id="stash-submit-form"
          button={renderSubmitButton()}
        />
        {renderSubControls(true)}
      </>
    );
  }

  return (
    <>
      <StashConfirm
        stash={stashAction}
        huggg={huggg}
        isSuperhugggEmailClaim={isSuperhugggEmailClaim}
        isSuperhugggSMSClaim={isSuperhugggSMSClaim}
        stashing={stashing}
        error={error}
        email={email}
        number={number}
        cancel={
          isSuperhugggEmailClaim ? changeRecipientEmail : changeRecipientNumber
        }
      />
      {renderSubControls()}
    </>
  );
};

InputScreen.propTypes = {
  changeRecipientEmail: PropTypes.func.isRequired,
  changeRecipientNumber: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  isSuperhugggEmailClaim: PropTypes.bool.isRequired,
  isSuperhugggSMSClaim: PropTypes.bool.isRequired,
  setRecipientEmail: PropTypes.func.isRequired,
  setRecipientNumber: PropTypes.func.isRequired,
  stashAction: PropTypes.func.isRequired,
  stashing: PropTypes.bool.isRequired,
  view: PropTypes.string.isRequired,
  additionalActions: PropTypes.node,
  error: PropTypes.string,
  number: PropTypes.string,
  email: PropTypes.string,
};

const Stash = (props) => {
  const {
    additionalActions,
    huggg,
    isStashed,
    isSuperhugggEmailClaim,
    isSuperhugggSMSClaim,
    view,
  } = props;

  const redirectPath = huggg.isSuperhuggg
    ? `/super/${huggg.shortcode}/stashed`
    : `/${huggg.shortcode}/stashed`;

  if (isStashed) {
    return <Redirect to={{ pathname: redirectPath }} />;
  }

  // TODO replace buttons here...

  return (
    <div
      className={classnames(
        'container',
        styles.stash,
        styles['stash-redesign'],
      )}
      data-test-id="stash-container"
    >
      <Divider />

      <StashTitle
        huggg={huggg}
        isSuperhugggEmailClaim={isSuperhugggEmailClaim}
        isSuperhugggSMSClaim={isSuperhugggSMSClaim}
        view={view}
      />

      <Divider />

      <InputScreen {...props} />
      {additionalActions}
    </div>
  );
};

Stash.propTypes = {
  additionalActions: PropTypes.element,
  huggg: PropTypes.object.isRequired,
  view: PropTypes.string.isRequired,
  stashAction: PropTypes.func.isRequired,
  isStashed: PropTypes.bool.isRequired,
  isSuperhugggEmailClaim: PropTypes.bool.isRequired,
  isSuperhugggSMSClaim: PropTypes.bool.isRequired,
};

export default Stash;
